import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, LoadingSpinner, ViewPageHeader } from '@components/common';
import { Collection } from '@components/common/Collection';
import { BaseModal } from '@components/modals';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';
import Icon from '@components/Icon/Icon';
import api from '@services/api';
import './TagViewPage.scss';

const TagViewPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const notify = useNotification();
    const [tag, setTag] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalState, setModalState] = useState({ isOpen: false, mode: 'delete' });

    useEffect(() => {
        const fetchTag = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/tags/${id}`);
                setTag(response.data);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch tag:', err);
                setError(t('tagView.fetchError'));
                notify.error(t('tagView.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchTag();
        }
    }, [id, t, notify]);

    const handleBack = () => {
        navigate('/tags');
    };

    const handleEdit = () => {
        navigate(`/tags/${id}/edit`);
    };

    const handleDelete = () => {
        setModalState({ isOpen: true, mode: 'delete' });
    };

    const handleModalClose = () => {
        setModalState({ isOpen: false, mode: 'delete' });
    };

    const handleModalSuccess = () => {
        if (modalState.mode === 'delete') {
            navigate('/tags');
        }
    };

    if (loading) {
        return (
            <div className="tag-view-page">
                <div className="loading-container">
                    <LoadingSpinner />
                    <p>{t('tagView.loading')}</p>
                </div>
            </div>
        );
    }

    if (error || !tag) {
        return (
            <div className="tag-view-page">
                <Card className="error-card">
                    <div className="error-content">
                        <h2>{t('tagView.errorTitle')}</h2>
                        <p>{error || t('tagView.notFound')}</p>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="tag-view-page">
            <ViewPageHeader
                onBack={handleBack}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />

            <div className="tag-details">
                <Card className="tag-info-card">
                    <div className="tag-header">
                        <div
                            className="tag-badge"
                            style={{ backgroundColor: tag.color }}
                        >
                            <span style={{
                                color: tag.color === '#ffffff' || tag.color === '#fff' ? '#000000' : '#ffffff'
                            }}>
                                <Icon icon="tag" size="lg" />
                            </span>
                        </div>
                        <div className="tag-info">
                            <h1>{tag.name}</h1>
                            {tag.description && (
                                <p className="tag-description">{tag.description}</p>
                            )}
                        </div>
                    </div>

                    <div className="tag-metadata">
                        <div className="metadata-item">
                            <span className="label">{t('tagView.color')}:</span>
                            <div className="color-display">
                                <div
                                    className="color-swatch"
                                    style={{ backgroundColor: tag.color }}
                                ></div>
                                <span className="color-code">{tag.color}</span>
                            </div>
                        </div>
                        <div className="metadata-item">
                            <span className="label">{t('tagView.createdAt')}:</span>
                            <span className="value">
                                {new Date(tag.created_at).toLocaleDateString()}
                            </span>
                        </div>
                        {tag.updated_at && (
                            <div className="metadata-item">
                                <span className="label">{t('tagView.updatedAt')}:</span>
                                <span className="value">
                                    {new Date(tag.updated_at).toLocaleDateString()}
                                </span>
                            </div>
                        )}
                    </div>
                </Card>

                <div className="tag-resources">
                    <Collection
                        modelName="resources"
                        displayType="grid"
                        title={t('tagView.resourcesWithTag')}
                        description={t('tagView.resourcesDescription', { tagName: tag.name })}
                        queryParams={{ tag_id: tag.id }}
                        enableSearch={true}
                        enablePagination={true}
                        className="tag-resources-collection"
                    />
                </div>
            </div>

            <BaseModal
                isOpen={modalState.isOpen}
                onClose={handleModalClose}
                onSuccess={handleModalSuccess}
                mode={modalState.mode}
                item={tag}
                itemType="tag"
                deleteFn={async (id) => await api.delete(`/tags/${id}`)}
                size="small"
            />
        </div>
    );
};

export default TagViewPage;
